'use client';

/**
 * Fires an addToCart event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { fireEvent } = useAddToCartEvent();
 * fireEvent(items);
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { getValueOrElseEmptyString } from 'utils/string';
import {
  ADD_TO_CART_GA4,
  CHECKFITMENT,
  DEFAULT_BRAND,
  DEFAULT_BUSINESS_UNIT
} from 'constants/analyticsConstants';
import { ItemPrice } from 'services/order/types';

type AddToCartType = {
  product: { [key: string]: unknown };
  cartId?: string;
  currencyCode?: string;
  dealerId?: string | number;
  model?: string;
  paymentMethods?: string[];
  storeName: string;
  list: string;
};

export const useAddToCartEvent = () => {
  const buildEvent = useCallback(
    ({ cartId, currencyCode, product, list }: AddToCartType) => {
      const cartDetails = {
        cartId: getValueOrElseEmptyString(cartId)
      };

      const priceWithoutGST = product.priceWithoutGST as ItemPrice;
      const productName = product.name ?? product.partName;
      const parseCurrency = (value: unknown): number => {
        if (typeof value === 'string') {
          return parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
        }
        return 0;
      };
      const subTotal =
        parseCurrency(product?.totalPrice) ||
        parseCurrency(product?.discountedPrice) ||
        parseCurrency(priceWithoutGST?.formatted?.totalPrice) ||
        0;
      const quantity =
        typeof product?.quantity === 'number'
          ? product.quantity
          : parseInt(String(product?.quantity || 1), 10);

      const addedProduct = {
        affiliation: DEFAULT_BUSINESS_UNIT,
        coupon: undefined,
        discount: parseCurrency(product?.discountedAmount),
        index: '',
        item_brand: DEFAULT_BRAND,
        item_category: getValueOrElseEmptyString(
          product.category || product.categoryPath
        ),
        item_category2: undefined,
        item_category3: undefined,
        item_category4: undefined,
        item_category5: undefined,
        item_id: getValueOrElseEmptyString(product.partNumber),
        item_name: getValueOrElseEmptyString(
          `${product.partNumber}: ${productName ?? product.description}`
        ),
        item_variant: undefined,
        location_id: getValueOrElseEmptyString(product.locationId),
        item_list_id: getValueOrElseEmptyString(list),
        item_list_name: getValueOrElseEmptyString(list),
        p_attributes_visible:
          product.productAttributesVisible != '' &&
          product.productAttributesVisible != null
            ? product.productAttributesVisible
            : undefined,
        p_check_fitment: CHECKFITMENT,
        p_inventory_status: product?.availability[0]?.availabilityMessage,
        p_kit_id: getValueOrElseEmptyString(product.kitId),
        p_serial_number: getValueOrElseEmptyString(product.serialNumber),
        p_weight: getValueOrElseEmptyString(product.weight),
        price:
          parseCurrency(product?.totalPrice) ||
          parseCurrency(product?.discountedPrice) ||
          0,
        quantity
      };

      return {
        event: ADD_TO_CART_GA4,
        cartDetails,
        ecommerce: {
          currency: getValueOrElseEmptyString(currencyCode),
          value: subTotal,
          items: [addedProduct]
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fireEvent = useCallback(
    (data: AddToCartType) => {
      sendGTMEvent(buildEvent(data));
    },
    [buildEvent]
  );

  return { fireEvent };
};
